import { html } from '../lib/html.js';
import { busy, toastError, toastSuccess } from '../lib/ui.js';
import { REJECTION_TEXT, vehicles } from '../services/vehicles.js';
import { Page, go } from './base.js';
import { bindDocumentConfirmation, documentConfirmation, bindUpload, uploadBox } from './vehicle-upload.js';

const fmtDay = (v) => new Date(v).toLocaleDateString('pt-BR');

/**
 * /vehicles/:vehicleId/comprovar — dono provisório envia o CRLV-e no nome dele.
 * Abre (ou continua) o envio do documento deste veículo e mostra a caixa de envio.
 * Aprovado pelo admin, "Dono provisório" vira "Dono" (sem transferência: o veículo já é dele no app).
 */
class VehicleProofPage extends Page {
  template() {
    return html`
      <ion-header class="ion-no-border">
        <ion-toolbar>
          <ion-buttons slot="start"><ion-back-button text="Voltar"></ion-back-button></ion-buttons>
          <ion-title>Comprovar o dono</ion-title>
        </ion-toolbar>
      </ion-header>
      <ion-content class="ion-padding">
        <div class="narrow body wiz"></div>
      </ion-content>`;
  }

  get vid() {
    if (this.vehicleId) return String(this.vehicleId);
    return decodeURIComponent(location.pathname.match(/\/vehicles\/([^/?#]+)/)?.[1] ?? '');
  }

  setup() {
    this.$('.body').addEventListener('click', (e) => {
      const el = e.target.closest('[data-action]');
      if (el?.dataset.action === 'start') this.start(el);
      if (el?.dataset.action === 'vehicle') go(`/vehicles/${this.vid}`, 'back');
    });
  }

  async onEnter() {
    this.$('ion-back-button').defaultHref = `/vehicles/${this.vid}`;
    const body = this.$('.body');
    body.innerHTML = '<div class="center"><ion-spinner></ion-spinner></div>';
    try {
      this.data = await vehicles.get(this.vid);
      this.render();
    } catch (err) {
      body.innerHTML = html`<div class="center"><ion-icon name="alert-circle-outline" class="empty-icon"></ion-icon><p>${err.message}</p></div>`.value;
    }
  }

  render() {
    const { vehicle: v, claims } = this.data;
    const claim = claims[0] ?? null;
    const body = this.$('.body');
    if (claim?.status === 'PENDING_UPLOAD') return this.renderUpload(claim);
    if (claim?.status === 'PENDING_REVIEW') {
      body.innerHTML = html`
        <div class="wiz-icon"><ion-icon name="time-outline"></ion-icon></div>
        <h1 class="wiz-title">Documento em análise</h1>
        <p class="wiz-lead">Enviado em ${fmtDay(claim.uploadedAt ?? claim.createdAt)}. Quando a análise aprovar, você deixa de ser dono provisório e passa a ser o dono ${v.plateFormatted ? `do ${v.plateFormatted}` : 'deste veículo'} no app.</p>
        <div class="wiz-foot"><ion-button expand="block" fill="outline" data-action="vehicle">Voltar ao veículo</ion-button></div>`.value;
      return;
    }
    body.innerHTML = html`
      <div class="wiz-icon"><ion-icon name="document-text-outline"></ion-icon></div>
      <h1 class="wiz-title">${v.category === 'AGRO' && !v.plate ? 'Envie o documento no seu nome' : 'Envie o CRLV-e no seu nome'}</h1>
      <p class="wiz-lead">${v.category === 'AGRO' && !v.plate ? 'Envie a nota fiscal (ou o documento de transferência) da máquina no seu nome para concluir a transferência no TicketCar.' : html`Depois que o Detran transferir o ${v.plateFormatted ?? 'veículo'} para você, o CRLV-e sai no seu nome. Envie aqui para concluir a transferência no TicketCar.`}</p>
      ${claim?.status === 'REJECTED'
        ? html`<div class="panel notice danger"><span class="row-icon danger"><ion-icon name="alert-circle-outline"></ion-icon></span><div><strong>O último envio foi recusado</strong><p>${REJECTION_TEXT[claim.rejectionCode] ?? REJECTION_TEXT.OTHER}</p>${claim.reason ? html`<p class="reason"><b>Observação:</b> ${claim.reason}</p>` : ''}</div></div>`
        : ''}
      <p class="wiz-hint left">Enquanto isso, o veículo continua com você como dono provisório: lembretes e empresas funcionam normalmente.</p>
      <div class="wiz-foot"><ion-button expand="block" data-action="start"><ion-icon slot="start" name="document-attach-outline"></ion-icon>Enviar o documento</ion-button></div>`.value;
  }

  async start(button) {
    await busy(button, async () => {
      try {
        const v = this.data.vehicle;
        if ((v.category ?? 'LIGHT') === 'LIGHT' && (await vehicles.registrationConfig()).vioEnabled) return go('/vehicles/new?tipo=LIGHT');
        const r = await vehicles.createClaim({ category: v.category ?? 'LIGHT', ...(v.plate ? { plate: v.plate } : {}), ...(v.renavam ? { renavam: v.renavam } : {}), ...(v.serialNumber ? { serialNumber: v.serialNumber } : {}) });
        this.renderUpload(r.claim);
      } catch (e) {
        if (e.code === 'VIO_SCAN_REQUIRED') return go('/vehicles/new?tipo=LIGHT');
        if (e.code === 'CLAIM_EXISTS') {
          this.data = await vehicles.get(this.vid);
          return this.render();
        }
        toastError(e.message);
      }
    });
  }

  renderUpload(claim) {
    if (claim.documentReady) {
      this.$('.body').innerHTML = documentConfirmation(claim).value;
      bindDocumentConfirmation(this, claim, () => go(`/vehicles/${this.vid}`, 'back'));
      return;
    }
    this.$('.body').innerHTML = html`
      <h1 class="wiz-title">CRLV-e no seu nome</h1>
      ${uploadBox({ category: this.data?.vehicle?.category })}`.value;
    bindUpload(this, claim.id, () => {
      toastSuccess('Documento enviado! Avisaremos quando a análise terminar.');
      go(`/vehicles/${this.vid}`, 'back');
    });
  }
}

customElements.define('page-vehicle-proof', VehicleProofPage);
